import { useCallback, useRef, useState } from 'preact/hooks';
import Button from '../../button/Button';
import './ProductCardButtons.less';
import Popover from '../../popover/Popover';
import ActionList from '../../list/ActionList';
import ProductAnchorButton from '../ProductAnchorButton';
import { useTranslation } from '../../../hooks/useTranslation';
import IconButton from '../../button/IconButton';

export type ProductCardButtonsAction =
    | 'compare'
    | 'product-search-similar'
    | 'product-search-combinations'
    | 'product-mix-and-match'
    | 'product-detail';

export type ProductCardButtonsProps = {
    isComparingMode: boolean;
    isComparisonFull: boolean;
    isSelectedForComparison: boolean;
    onAction: (action: ProductCardButtonsAction) => void;
};

const ProductCardButtons = ({
    isComparingMode,
    isComparisonFull,
    isSelectedForComparison,
    onAction
}: ProductCardButtonsProps) => {
    const { t } = useTranslation();
    const [isOpen, setOpen] = useState(false);
    const anchorRef = useRef<HTMLButtonElement>(null);

    const onAnchorClick = useCallback(
        (event: MouseEvent) => {
            event.stopPropagation();
            event.preventDefault();
            setOpen(!isOpen);
        },
        [isOpen]
    );

    const onActionClick = useCallback(
        (event: MouseEvent, action: Parameters<typeof onAction>[0]) => {
            event.stopPropagation();
            event.preventDefault();
            setOpen(false);
            onAction(action);
        },
        [onAction]
    );

    return (
        <div
            className={`bri-ProductCardButtons ${
                isComparingMode ? 'bri-comparing' : ''
            }`}
        >
            {!isComparingMode && (
                <ProductAnchorButton
                    selected={isOpen}
                    onClick={onAnchorClick}
                    ref={anchorRef}
                />
            )}
            {isComparingMode && !isSelectedForComparison && (
                <Button
                    variant="glass"
                    title={
                        isComparisonFull
                            ? t('PRODUCT_CARD_COMPARISON_LIMIT_TITLE')
                            : ''
                    }
                    disabled={isComparisonFull}
                    className={`bri-ProductCardButtons-featured-button bri-compare-button`}
                    startIconSize="small"
                    onClick={event => onActionClick(event, 'compare')}
                >
                    {t('PRODUCT_CARD_COMPARISON_UNSELECTED_BUTTON_LABEL')}
                </Button>
            )}
            {isComparingMode && isSelectedForComparison && (
                <IconButton
                    variant="glass"
                    size="medium"
                    className={`bri-ProductCardButtons-featured-button bri-compare-button`}
                    iconId="close"
                    iconSize="x-small"
                    onClick={event => onActionClick(event, 'compare')}
                />
            )}
            <Popover
                isOpen={isOpen}
                anchorRef={anchorRef}
                onClose={() => setOpen(false)}
            >
                <ActionList
                    items={[
                        {
                            id: 'product-detail',
                            label: t('PRODUCT_CARD_QUESTION_BUTTON_LABEL'),
                            iconId: 'question'
                        },
                        {
                            id: 'compare',
                            label: t('PRODUCT_CARD_COMPARISON_BUTTON_LABEL'),
                            iconId: 'compare'
                        },
                        {
                            id: 'product-search-similar',
                            label: t('PRODUCT_CARD_SIMILAR_BUTTON_LABEL'),
                            iconId: 'similar'
                        },
                        {
                            id: 'product-search-combinations',
                            label: t('PRODUCT_CARD_COMBINATIONS_BUTTON_LABEL'),
                            iconId: 'join'
                        },
                        {
                            id: 'product-mix-and-match',
                            label: t('PRODUCT_CARD_MIX_AND_MATCH_BUTTON_LABEL'),
                            iconId: 'outfit'
                        }
                    ]}
                    onClick={(event, item) => onActionClick(event, item.id)}
                />
            </Popover>
        </div>
    );
};

export default ProductCardButtons;
